import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axiosInstance.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('forgotPasswordError', 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800">
      <header className="p-4 flex justify-between items-center">
        <button onClick={() => navigate('/signin')} className="flex items-center text-white hover:text-white/80" data-testid="back-button">
          <ArrowLeft className="h-6 w-6 mr-2" />
        </button>
        <LanguageToggle />
      </header>

      <div className="flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-password-success">
              <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-3">{t('checkYourEmail', 'Check your email')}</h1>
              <p className="text-gray-600 mb-6 text-sm">
                {t('resetEmailSent', 'If an account exists for that email, we\'ve sent a password reset link. Please check your inbox and spam folder.')}
              </p>
              <Link to="/signin" className="text-purple-600 hover:text-purple-700 font-semibold text-sm" data-testid="back-to-signin">
                {t('backToSignIn', 'Back to Sign In')}
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <Mail className="h-10 w-10 text-purple-500 mb-3" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="forgot-password-title">
                  {t('forgotPasswordTitle', 'Forgot your password?')}
                </h1>
                <p className="text-gray-600 text-sm">
                  {t('forgotPasswordDesc', 'Enter your email address and we\'ll send you a link to reset your password.')}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('emailAddress', 'Email address')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? t('sending', 'Sending...') : t('sendResetLink', 'Send Reset Link')}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/signin" className="text-sm text-gray-600 hover:text-purple-600">
                  {t('backToSignIn', 'Back to Sign In')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
